/**
 * 画布自定义节点（P1 C-2b）：把 CanvasNode 渲染成一张带左右连接点的卡片。
 * 刻意不用 .panel（那有悬停上浮，会干扰拖拽手感），用独立的节点卡样式。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';

import type { NodeKind, NodeMode } from '../../model/canvas';

export interface CanvasFlowNodeData extends Record<string, unknown> {
  title: string;
  kind: NodeKind;
  mode: NodeMode;
  hint?: string | undefined;
  /** 媒体缩略图（图像=自身、视频=首帧）；有则卡片顶部渲染预览（D-51） */
  thumb?: string | undefined;
  /** 大图 url（点开预览用，缺省回退 thumb） */
  full?: string | undefined;
  /** 视频时长（秒）；有则在缩略图右下角显时长角标 */
  durationSec?: number | undefined;
  /** 文本生成结果摘要（如“剧本 · 1 集 / 2 场”）；有则标题下显示 */
  preview?: string | undefined;
}

/** 秒 → m:ss（时长角标用） */
function formatDur(s: number): string {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, '0')}`;
}

const KIND_LABEL: Record<NodeKind, string> = {
  text: '文本',
  image: '图像',
  shot: '分镜',
  video: '视频',
  audio: '音频',
  subject: '主体',
  timeline: '时间线',
  director: '导演台',
  skill: '技能',
  group: '组合',
};

/** 图像类按模式区分：generator=分镜关键帧、derived=视图、source=图片。 */
function kindLabel(kind: NodeKind, mode: NodeMode): string {
  if (kind === 'image') {
    return mode === 'generator' ? '分镜关键帧' : mode === 'derived' ? '视图' : '图片';
  }
  return KIND_LABEL[kind];
}

const MODE_LABEL: Record<NodeMode, string> = {
  source: '素材',
  generator: '生成',
  derived: '派生',
};

const MODE_CLASS: Record<NodeMode, string> = {
  source: 'border-surface-edge text-ink-secondary',
  generator: 'border-accent/60 text-accent',
  derived: 'border-state-info/60 text-state-info',
};

export function CanvasNodeCard({ data }: NodeProps) {
  const d = data as CanvasFlowNodeData;
  const [zoom, setZoom] = useState(false);
  return (
    <div
      className="canvas-node-card min-w-[160px] max-w-[220px] rounded-card border bg-surface-raised px-3 py-2 shadow-[0_2px_10px_rgba(0,0,0,.4)] transition-all duration-150"
    >
      <Handle type="target" position={Position.Left} className="canvas-handle">
        <span className="canvas-handle-plus">+</span>
      </Handle>
      <div className="mb-0.5 flex items-center justify-between gap-2">
        <span className="text-uiSm text-ink-muted">{kindLabel(d.kind, d.mode)}</span>
        <span className={`rounded-full border px-1.5 text-[10px] ${MODE_CLASS[d.mode]}`}>
          {MODE_LABEL[d.mode]}
        </span>
      </div>
      {d.kind === 'video' && d.full !== undefined ? (
        <div className="mb-1.5 overflow-hidden rounded-control border border-surface-line">
          <video
            src={d.full}
            poster={d.thumb}
            controls
            preload="metadata"
            className="block h-24 w-full bg-black object-cover"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : d.kind === 'audio' && d.full !== undefined ? (
        <div className="mb-1.5">
          <audio src={d.full} controls preload="metadata" className="w-full" onClick={(e) => e.stopPropagation()} />
        </div>
      ) : d.thumb !== undefined ? (
        <div className="relative mb-1.5 overflow-hidden rounded-control border border-surface-line">
          <img
            src={d.thumb}
            alt=""
            className="block h-24 w-full cursor-zoom-in object-cover"
            onClick={(e) => {
              e.stopPropagation();
              setZoom(true);
            }}
          />
          {d.kind === 'video' && d.durationSec !== undefined && (
            <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[10px] text-white">
              {formatDur(d.durationSec)}
            </span>
          )}
        </div>
      ) : null}
      {zoom && d.thumb !== undefined &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6"
            onClick={(e) => {
              e.stopPropagation();
              setZoom(false);
            }}
            role="presentation"
          >
            <img src={d.full ?? d.thumb} alt="" className="max-h-full max-w-full rounded-card object-contain" />
            <span className="absolute right-4 top-4 rounded-control bg-surface-overlay px-3 py-1 text-ui text-ink-primary">✕ 关闭</span>
          </div>,
          document.body,
        )}
      <div className="text-ui font-semibold text-ink-primary">{d.title}</div>
      {d.preview !== undefined && (
        <div className="mt-0.5 text-uiSm text-ink-secondary">{d.preview}</div>
      )}
      {d.hint !== undefined && (
        <div className="mt-0.5 text-uiSm leading-snug text-ink-muted">{d.hint}</div>
      )}
      <Handle type="source" position={Position.Right} className="canvas-handle">
        <span className="canvas-handle-plus">+</span>
      </Handle>
    </div>
  );
}
